import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, Heart, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DonateSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [paymentDetails, setPaymentDetails] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        try {
          const response = await axios.get(`${API}/donations/status/${sessionId}`);
          
          if (response.data.payment_status === "paid") {
            setStatus("success");
            setPaymentDetails(response.data);
            return;
          } else if (response.data.status === "expired") {
            setStatus("expired");
            return;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(poll, pollInterval);
          } else {
            setStatus("pending");
            setPaymentDetails(response.data);
          }
        } catch (error) {
          console.error("Error checking payment status:", error);
          if (attempts < maxAttempts) {
            attempts++;
            setTimeout(poll, pollInterval);
          } else {
            setStatus("error");
          }
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId]);

  return (
    <div data-testid="donate-success-page" className="min-h-[70vh] flex items-center justify-center py-16">
      <div className="max-w-lg mx-auto px-4">
        {status === "checking" && (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-[#0056D2] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              Processing Your Donation...
            </h1>
            <p className="text-slate-600">Please wait while we confirm your payment.</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              Thank You for Your Donation!
            </h1>
            <p className="text-lg text-slate-600 mb-6">
              Your generous contribution of{" "}
              <span className="font-semibold text-[#0056D2]">
                ${paymentDetails?.amount_total ? (paymentDetails.amount_total / 100).toFixed(2) : ""}
              </span>{" "}
              will help us continue our mission of building peace in communities.
            </p>
            <div className="bg-[#F0F9FF] rounded-2xl p-6 mb-8">
              <p className="text-slate-600">
                A confirmation email will be sent to your email address. Your donation is tax-deductible.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/">
                <Button className="btn-primary">
                  Return Home
                </Button>
              </Link>
              <Link to="/volunteer">
                <Button variant="outline" className="btn-secondary">
                  Become a Volunteer
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        {status === "pending" && (
          <div className="text-center">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-yellow-500" />
            </div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              Payment Processing
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Your payment is being processed. You'll receive a confirmation email once complete.
            </p>
            <Link to="/">
              <Button className="btn-primary">Return Home</Button>
            </Link>
          </div>
        )}

        {(status === "error" || status === "expired") && (
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-red-400" />
            </div>
            <h1 className="text-3xl font-bold font-['Outfit'] text-[#0F172A] mb-4">
              {status === "expired" ? "Session Expired" : "Something Went Wrong"}
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              {status === "expired" 
                ? "Your payment session has expired. Please try again."
                : "We couldn't verify your payment. If you completed the payment, please contact us."
              }
            </p>
            <Link to="/donate">
              <Button className="btn-donate">
                <Heart className="w-5 h-5 mr-2" />
                Try Again
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
